// HTTP-клиент поверх fetch, общий для клиентов бэкенда и GIS.
//
// После разделения репозиториев за каждым корнем путей стоит свой сервис
// (SPLIT_PLAN 4.1), но обработка ошибок одна: сервер отдаёт `detail` строкой
// или объектом с `message`, и в интерфейс должно попасть человеческое
// сообщение, а не сырой JSON.

/** Текст ошибки из `detail` ответа: строка как есть, объект — его `message`. */
export function messageFromDetail(detail: unknown, status: number): string {
  const message =
    typeof detail === 'string'
      ? detail
      : typeof detail === 'object' && detail !== null && 'message' in detail
        ? String((detail as { message: unknown }).message)
        : JSON.stringify(detail);
  return message || `HTTP ${status}`;
}

/**
 * Ошибка ответа сервера: текст для человека плюс статус и исходный `detail`,
 * чтобы вызывающий мог распознать структурированный ответ (например, 422
 * `dimension_selection_required`), не разбирая текст сообщения.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: unknown,
  ) {
    super(messageFromDetail(detail, status));
    this.name = 'ApiError';
  }
}

export type Request = <T>(path: string, init?: RequestInit) => Promise<T>;

/**
 * Подключение входа к HTTP-клиенту (src/auth.ts). Без него запросы уходят
 * как раньше — без заголовков авторизации (AUTH_MODE=disabled).
 */
export interface RequestAuth {
  /** Заголовки авторизации для адреса: Bearer или пропуск ГИС; пусто — без них. */
  headers(url: string): Record<string, string>;
  /**
   * Ответ 401 на запрос с токеном: обновить токен. true — можно повторить
   * запрос; false — вход потерян (клиент уже перешёл в анонимный режим).
   */
  refresh(): Promise<boolean>;
}

let requestAuth: RequestAuth | null = null;

export function setRequestAuth(auth: RequestAuth | null): void {
  requestAuth = auth;
}

/** Заголовки авторизации для адреса — для запросов не через createRequest (тайлы, каталог Martin). */
export function authHeaders(url: string): Record<string, string> {
  return requestAuth?.headers(url) ?? {};
}

async function errorFromResponse(response: Response): Promise<ApiError> {
  const text = await response.text();
  let detail: unknown = text || response.statusText;
  try {
    const body = text ? JSON.parse(text) as { detail?: unknown } : null;
    detail = body && typeof body === 'object' && 'detail' in body ? body.detail : body ?? detail;
  } catch {}
  return new ApiError(response.status, detail);
}

export function createRequest(root: string): Request {
  return async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${root}${path}`;
    const send = (auth: Record<string, string>): Promise<Response> => fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...auth,
        ...(init?.headers ?? {}),
      },
    });
    const auth = authHeaders(url);
    let response = await send(auth);
    // 401 на запрос с токеном — токен истёк или отозван: одна попытка
    // обновить его и повторить. Без токена повторять нечего.
    if (response.status === 401 && 'Authorization' in auth && requestAuth && await requestAuth.refresh()) {
      response = await send(authHeaders(url));
    }
    if (!response.ok) throw await errorFromResponse(response);
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  };
}
